/* eslint-disable no-nested-ternary */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
  Button,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import IconButton from '@mui/material/IconButton';
import { useNavigate } from 'react-router-dom';
import { Add } from '@mui/icons-material';
import TableSkeleton from '../../components/common/TableSkeleton';
import PrivateWrapper from '../../layouts/Private';
import RoutePaths from '../../configs/Routes';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import Status from '../../components/common/DataStatus';
import { deleteBlogById, getBlogs } from '../../services/Blogs';
import useToastr from '../../hooks/useToastr';

const BlogList = () => {
  const pageName = 'Blogs';
  const navigate = useNavigate();
  const { showSuccessToastr, showErrorToastr } = useToastr();

  const [rows, setRows] = useState([]);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [processing, setProcessing] = useState(false);

  const [reloadRows, setReloadRows] = useState(false);

  const columns = [
    { name: 'Title', id: 'title', width: '25%' },
    { name: 'Creator', id: 'email', width: '10%' },
    { name: 'Created At', id: 'promocodes', width: '15%' },
    { name: 'Status', id: 'status', width: '5%', align: 'center' },
    { name: 'Actions', id: 'action', width: '5%', align: 'center' },
  ];

  const handleOpenConfirmDialog = (id) => {
    setSelectedId(id);
    setOpenConfirmDialog(true);
  };

  const handleCloseConfirmDialog = () => {
    setSelectedId(null);
    setOpenConfirmDialog(false);
  };

  useEffect(() => {
    setProcessing(false);
    setDataLoaded(false);
    getBlogs()
      .then((res) => {
        setRows(
          res.map((r) => ({
            id: r.id,
            title: r.title,
            creator: r.creator,

            status: r.status,
          }))
        );
        // setTotalPages(res.data.totalPages);
        setDataLoaded(true);
      })
      .catch(() => {
        setRows([]);
        setDataLoaded(true);
      });
  }, [reloadRows]);

  const handleCreate = () => navigate(RoutePaths.CREATE_BLOG);

  const handleBlogDelete = async () => {
    try {
      setProcessing(true);
      await deleteBlogById(selectedId);
      showSuccessToastr('Blog deleted sucessfully.');
      setSelectedId(null);
      setOpenConfirmDialog(false);
      setReloadRows(true);
    } catch (e) {
      showErrorToastr(e.message || 'Something went wrong.');
    }
  };

  return (
    <PrivateWrapper pageName={pageName}>
      <Box px={2}>
        <Box display="flex" mt={4}>
          <>
            <div />
            <Box ml="auto">
              <Button
                startIcon={<Add />}
                variant="contained"
                disableElevation
                onClick={handleCreate}
              >
                Create
              </Button>
            </Box>
          </>
        </Box>
        <TableContainer sx={{ my: 2 }}>
          <Table aria-label="simple table">
            <TableHead sx={{ bgcolor: '#E8E8E8' }}>
              <TableRow>
                {columns.map((col) => (
                  <TableCell width={col.width} align={col.align || 'left'} key={`col-${col.id}`}>
                    {col.name}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {!dataLoaded && <TableSkeleton cols={5} />}
              {dataLoaded && rows.length === 0 && (
                <TableCell align="center" size="medium" colSpan={5}>
                  No records found
                </TableCell>
              )}
              {dataLoaded &&
                rows.map((row) => (
                  <TableRow key={`sb-${row.id}`}>
                    <TableCell>{row.title}</TableCell>
                    <TableCell>{row.creator}</TableCell>
                    <TableCell>{row.createdAt}</TableCell>
                    <TableCell align="center">
                      <Status value={row.status} statusFor="blog" />
                    </TableCell>
                    <TableCell align="center">
                      <BlogActions
                        blogId={row.id}
                        deleteBlog={() => {
                          handleOpenConfirmDialog(row.id);
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        {openConfirmDialog && (
          <ConfirmDialog
            title="Delete Blog"
            message="Are you sure want to delete selected blog?"
            confirmBtnText="Delete"
            onClose={handleCloseConfirmDialog}
            onConfirm={handleBlogDelete}
            editId={selectedId}
            processing={processing}
          />
        )}
      </Box>
    </PrivateWrapper>
  );
};

const BlogActions = ({ blogId, deleteBlog }) => {
  const navigate = useNavigate();

  return (
    <Box display="flex">
      <IconButton
        onClick={() => {
          navigate(`${RoutePaths.EDIT_BLOG.replace(':id', blogId)}`);
        }}
      >
        <EditIcon fontSize="small" />
      </IconButton>
      <IconButton>
        <DeleteIcon color="error" fontSize="small" onClick={deleteBlog} />
      </IconButton>
    </Box>
  );
};

BlogActions.propTypes = {
  blogId: PropTypes.string.isRequired,
  deleteBlog: PropTypes.func.isRequired,
};

export default BlogList;
